import { useEffect, useRef, useState } from "react";
import apiClient from "../Services/ApiClient";

interface CustomDataConfig extends RequestInit {
  // Only the newest request may touch state. Use on pages where a slower older
  // request (e.g. All Locations) must not overwrite the selection that replaced it.
  cancelPrevious?: boolean;
}

const useGetCustomData = (url: string, config: CustomDataConfig = {}) => {
  const { cancelPrevious = false, ...fetchConfig } = config;
  const [fieldData, setData]: any = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeRequest = useRef<AbortController | null>(null);

 const fetchData = async () => {
     const controller = cancelPrevious ? new AbortController() : null;
     if (controller) {
       activeRequest.current?.abort();
       activeRequest.current = controller;
     }
     const isStale = () => !!controller && activeRequest.current !== controller;

     setLoading(true);
     setError(null);
     try {
       const response = await apiClient(url, {
         method: 'GET',
         ...fetchConfig,
         ...(controller ? { signal: controller.signal } : {}),
       });
 
       if (!response.ok) {
         throw new Error(`Error: ${response.statusText}`);
       }
       const data = await response.json();
       if (!isStale()) {
         setData(data);
       }
     } catch (error: any) {
       if (controller?.signal.aborted) return;
       console.error("Error fetching data:", error);
       setError(error.message || "An error occurred while fetching data.");
     } finally {
       if (!isStale()) {
         setLoading(false);
       }
     }
   };
 
   useEffect(() => {
     fetchData();
     return () => activeRequest.current?.abort();
   }, [url]);

  return [fieldData, loading, error, fetchData];
};

export default useGetCustomData;
